// pages/Dashboard.js
import { useWeb3 } from "../context/Web3Context";
import { formatNumber, formatBalance, formatPrice } from "../utils/formatters";

// Constants for URLs - using your structure
const TOKEN_ADDRESS = "0x62CDe9Aa2159461F094e3eaCBa10F7C45C0A6B4f";
const LP_TOKEN_ADDRESS = "0x503FD866B57315C643EB14080e45B5B85ff9C183";

// Base URL for your DEX
const DEX_BASE_URL = "https://dex.goodog.io";

// Max supply constant for FDV calculation
const MAX_SUPPLY = 21000000;

export default function Dashboard() {
  const { 
    account, 
    ethBalance, 
    tokenBalance, 
    // IMPORTANT: You need to add 'totalSupply' to the context value
    totalSupply, // This should be the circulating supply from your contract
    lpTokenBalance,
    minePriceInETH,
    mineReserve,
    ethReserve,
    calculateLPValue,
    lpTotalSupply
  } = useWeb3();

  if (!account) {
    return (
      <div className="dashboard-container">
        <h1>Dashboard</h1>
        <p>Please connect your wallet to view your dashboard</p>
      </div>
    );
  }

  const lpValue = calculateLPValue ? calculateLPValue() : 0;
  
  // Calculate LP metrics
  const lpShare = parseFloat(lpTokenBalance) > 0 && parseFloat(lpTotalSupply) > 0 
    ? parseFloat(lpTokenBalance) / parseFloat(lpTotalSupply)
    : 0;

  const totalLiquidityETH = parseFloat(ethReserve) * 2;
  
  // Your share of each token in the pool
  const yourMineInPool = parseFloat(mineReserve) * lpShare;
  const yourETHInPool = parseFloat(ethReserve) * lpShare;

  // --- NEW CALCULATIONS FOR FDV AND MARKET CAP ---
  const minePriceNum = parseFloat(minePriceInETH) || 0;
  
  // Calculate FDV (max supply valuation)
  const fdvInETH = minePriceNum * MAX_SUPPLY;
  
  // Calculate Market Cap (circulating supply valuation)
  // You need to ensure 'totalSupply' is a number from your context
  const totalSupplyNum = parseFloat(totalSupply) || 0;
  const marketCapInETH = minePriceNum * totalSupplyNum;
  
  return (
    <div className="dashboard-container">
      <h1>Your Dashboard</h1>

      <div className="dashboard-grid">
        {/* Wallet Balances */}
        <div className="info-panel">
          <h2>👛 Wallet Balances</h2>
          <div className="balance-item">
            <span className="label">ETH:</span>
            <span className="value">{formatBalance(ethBalance)}</span>
          </div>
          <div className="balance-item">
            <span className="label">MINE:</span>
            <span className="value">{formatNumber(tokenBalance)}</span>
          </div>
          
          <div className="balance-item">
            <span className="label">Your MINE in LP</span>
            <span className="value">{formatNumber(yourMineInPool)} MINE</span>
          </div>
          {minePriceNum > 0 && (
            <div className="balance-item">
              <span className="label">MINE Value:</span>
              <span className="value">{formatBalance(parseFloat(tokenBalance) * minePriceNum)} ETH</span>
            </div>
          )}
          {lpValue > 0 && (
            <div className="balance-item">
              <span className="label">LP Value:</span>
              <span className="value highlight">{formatBalance(lpValue)} ETH</span>
            </div>
          )}

        </div>

        {/* MINE/ETH Pool Info */}
        <div className="info-panel">
          <h2>💧 MINE/ETH Pool</h2>
          <div className="pool-item">
            <span className="label">MINE Price:</span>
            <span className="value">{formatPrice(minePriceInETH)} ETH</span>
          </div>
          {/* --- NEW: FDV AND MARKET CAP DISPLAY --- */}
          {minePriceNum > 0 && (
            <>
              <div className="pool-item">
                <span className="label">Market Cap:</span>
                <span className="value">{formatBalance(marketCapInETH)} ETH</span>
              </div>
              <div className="pool-item">
                <span className="label">FDV:</span>
                <span className="value">{formatBalance(fdvInETH)} ETH</span>
              </div>
            </>
          )}
          <div className="pool-item">
            <span className="label">Pool Reserves:</span>
            <div className="reserves">
              <div className="reserve">
                <span className="token">MINE:</span>
                <span className="amount">{formatNumber(mineReserve)}</span>
              </div>
              <div className="reserve">
                <span className="token">ETH:</span>
                <span className="amount">{formatBalance(ethReserve)}</span>
              </div>
            </div>
          </div>
          <div className="pool-item">
            <span className="label">Total Liquidity:</span>
            <span className="value">{formatBalance(totalLiquidityETH)} ETH</span>
          </div>
       
        </div>

        {/* Quick Actions */}
        <div className="info-panel">
          <h2>⚡ Quick Actions</h2>
          <div className="action-buttons">
            <button 
              className="btn btn-secondary"
              onClick={() => window.open(`${DEX_BASE_URL}/#/swap?inputCurrency=eth&outputCurrency=${TOKEN_ADDRESS}`, '_blank')}
            >
              Buy MINE
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => window.open(`${DEX_BASE_URL}/#/swap?inputCurrency=${TOKEN_ADDRESS}&outputCurrency=eth`, '_blank')}
            >
              Sell MINE
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => window.open(`${DEX_BASE_URL}/#/add/v2/ETH/${TOKEN_ADDRESS}`, '_blank')}
            >
              Add Liquidity
            </button>
            {parseFloat(lpTokenBalance) > 0 && (
              <button 
                className="btn btn-secondary"
                onClick={() => window.open(`${DEX_BASE_URL}/#/remove/v2/ETH/${TOKEN_ADDRESS}`, '_blank')}
              >
                Remove Liquidity
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}